import { styled } from "linaria/react";

export type TCardAccent = "caller" | "agent" | "interpreter";
export type TStripeSide = "left" | "right";

// Magenta (not Element warning orange) so interpreter stays distinct for
// red/green color-impaired viewers alongside caller green + agent blue.
const accentColor = (accent: TCardAccent) => {
  if (accent === "caller") return "var(--el-color-success, #28a745)";
  if (accent === "interpreter") return "var(--convey-interpreter-accent, #c026a8)";
  return "var(--el-color-primary, #186dde)";
};

const accentBg = (accent: TCardAccent) => {
  if (accent === "caller") return "var(--el-color-success-light-9, rgba(40, 167, 69, 0.1))";
  if (accent === "interpreter") return "var(--convey-interpreter-accent-bg, rgba(192, 38, 168, 0.12))";
  return "var(--el-color-primary-light-9, rgba(24, 109, 222, 0.1))";
};

export const InterpretationLayout = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  padding: var(--el-card-padding, 0.75rem);
  background: var(--el-bg-color-page, var(--el-fill-color-lighter, #f5f7fa));
  overflow: hidden;
`;

export const FeedScroll = styled.div`
  box-sizing: border-box;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  padding-bottom: calc(0.5rem + var(--el-font-size-base, 14px) * 1.5);
`;

export const MessageCard = styled.div<{ accent: TCardAccent; stripe?: TStripeSide }>`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem 0.875rem;
  background: var(--el-bg-color, #ffffff);
  border: 1px solid var(--el-card-border-color, var(--el-border-color, #e4e7ed));
  /* Agent cards: no color banner — only a neutral border. */
  border-left: ${(props) => {
    if (props.accent === "agent") {
      return "1px solid var(--el-card-border-color, var(--el-border-color, #e4e7ed))";
    }
    return (props.stripe || "left") === "left"
      ? `3px solid ${accentColor(props.accent)}`
      : "1px solid var(--el-card-border-color, var(--el-border-color, #e4e7ed))";
  }};
  border-right: ${(props) => {
    if (props.accent === "agent") {
      return "1px solid var(--el-card-border-color, var(--el-border-color, #e4e7ed))";
    }
    return props.stripe === "right"
      ? `3px solid ${accentColor(props.accent)}`
      : "1px solid var(--el-card-border-color, var(--el-border-color, #e4e7ed))";
  }};
  border-radius: var(--el-card-border-radius, var(--el-border-radius-base, 8px));
  box-shadow: var(--el-box-shadow-lighter, 0 1px 2px rgba(0, 0, 0, 0.04));
`;

export const CardHeader = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

export const SpeakerBadge = styled.span<{ accent: TCardAccent }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: ${(props) => (props.accent === "agent" ? "2px 0" : "2px 0.625rem")};
  border-radius: var(--el-border-radius-round, 999px);
  font-size: var(--el-font-size-small, 12px);
  font-weight: ${(props) => (props.accent === "agent" ? 500 : 600)};
  line-height: 1.5;
  white-space: nowrap;
  /* Agent label deemphasized (~#ccc); caller/interpreter keep accent pills. */
  color: ${(props) =>
    props.accent === "agent"
      ? "var(--convey-agent-label, #ccc)"
      : accentColor(props.accent)};
  background: ${(props) => (props.accent === "agent" ? "transparent" : accentBg(props.accent))};

  svg {
    display: block;
    width: 0.75rem;
    height: 0.75rem;
    flex: 0 0 auto;
  }
`;

export const CardTimestamp = styled.span`
  font-size: var(--el-font-size-extra-small, 11px);
  color: var(--el-text-color-secondary, #909399);
  white-space: nowrap;
  flex: 0 0 auto;
`;

// `emphasized` slants a settled line (same convention as TranscribeNote); the
// agent leg uses it on its own English speech so a relayed non-English turn on
// that same leg stays upright and reads as a different speaker.
export const PrimaryText = styled.div<{
  muted?: boolean;
  interim?: boolean;
  emphasized?: boolean;
}>`
  font-size: var(--el-font-size-base, 14px);
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  color: ${(props) =>
    props.muted
      ? "var(--el-text-color-secondary, #909399)"
      : props.interim
        ? "var(--el-text-color-regular, #606266)"
        : "var(--el-text-color-primary, #303133)"};
  font-style: ${(props) => (props.interim || props.emphasized ? "italic" : "normal")};

  .interpretation-active-word {
    text-decoration: underline;
    text-decoration-style: dotted;
    text-underline-offset: 2px;
  }
`;

export const SecondaryDivider = styled.div`
  border-top: 1px dotted var(--el-border-color-lighter, #ebeef5);
  margin: 0.125rem 0;
`;

export const SecondaryRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
`;

export const LangBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  padding: 1px 0.375rem;
  border-radius: var(--el-border-radius-small, 4px);
  font-size: var(--el-font-size-extra-small, 11px);
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--el-text-color-secondary, #909399);
  background: var(--el-fill-color-light, #e9e9eb);
  line-height: 1.4;
`;

export const SecondaryText = styled.div<{ interim?: boolean; slant?: boolean }>`
  flex: 1 1 auto;
  min-width: 0;
  font-size: var(--el-font-size-small, 12px);
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  font-style: ${(props) => (props.slant === false ? "normal" : "italic")};
  color: var(--el-text-color-secondary, #909399);

  .interpretation-active-word {
    text-decoration: underline;
    text-decoration-style: dotted;
    text-underline-offset: 2px;
  }
`;

// Blinking caret shown at the tail of the actively-streaming utterance.
export const StreamingCursor = styled.span`
  display: inline-block;
  width: 2px;
  height: 1em;
  margin-left: 2px;
  vertical-align: text-bottom;
  background: var(--el-color-primary, #186dde);
  animation: streaming-cursor-blink 1s step-end infinite;

  @keyframes streaming-cursor-blink {
    0%,
    49% {
      opacity: 1;
    }
    50%,
    100% {
      opacity: 0;
    }
  }
`;

export const EmptyState = styled.div`
  padding: 0.75rem;
  font-size: var(--el-font-size-small, 12px);
  font-style: italic;
  color: var(--el-text-color-placeholder, #a8abb2);
  text-align: center;
`;
